import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowLeft, Eye, Github, ExternalLink, TrendingUp, Calendar, Target, Users, X, Filter, Code } from 'lucide-react';
import { projects } from '../data/projects';
import Header from './Header';
import Footer from './Footer';

const ProjectsPage = () => {
  const [filter, setFilter] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const [hoveredProject, setHoveredProject] = useState(null);

  // Use static data instead of API call
  const projectsData = projects;

  const getStatusBadgeColor = (status) => {
    switch(status) {
      case 'Completed': return 'bg-emerald-600 text-white';
      case 'In Progress': return 'bg-blue-600 text-white';
      case 'In Development': return 'bg-purple-600 text-white';
      case 'Planning': return 'bg-amber-600 text-white';
      default: return 'bg-gray-600 text-white';
    }
  };

  const categories = ['all', ...new Set(projectsData.map(p => p.category))];
  const filteredProjects = filter === 'all' ? projectsData : projectsData.filter(p => p.category === filter);

  const handleViewProject = (project) => {
    setSelectedProject(project);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      <Header />
      
      {/* Projects Page Content */}
      <div className="pt-20 pb-20">
        {/* Page Header */}
        <div className="max-w-7xl mx-auto px-6 mb-12">
          <div className="text-center mb-8">
            <Button 
              onClick={() => window.location.href = '/'}
              className="btn-base btn-secondary mb-6"
            >
              <ArrowLeft size={16} className="mr-2" />
              Back to Portfolio
            </Button>
            
            <h1 className="heading-1 text-text-primary mb-4">
              All Projects
            </h1>
            <p className="body-large text-text-secondary max-w-2xl mx-auto">
              Explore all my technical projects, process improvements, and innovative solutions 
              across various domains including AI, automation, and data analytics.
            </p>
          </div>

          {/* Filter Buttons */}
          <div className="flex justify-center mb-8">
            <div className="flex flex-wrap gap-3 p-1 bg-white rounded-xl shadow-sm border border-gray-200">
              {categories.map((category) => (
                <Button
                  key={category}
                  onClick={() => setFilter(category)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    filter === category
                      ? 'bg-purple-600 text-white shadow-md'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                  variant="ghost"
                >
                  <Filter size={16} className="mr-2" />
                  {category === 'all' ? 'All Projects' : category}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Projects Grid */}
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
              <Card 
                key={project.id}
                className={`group glass-card overflow-hidden transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl flex flex-col ${
                  hoveredProject === project.id ? 'ring-2 ring-purple-600/30' : ''
                }`}
                onMouseEnter={() => setHoveredProject(project.id)}
                onMouseLeave={() => setHoveredProject(null)}
              >
                {/* Project Image */}
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={project.image || '/api/placeholder/400/240'}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    onError={(e) => {
                      e.target.src = '/api/placeholder/400/240';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-white/90 via-transparent to-transparent"></div>
                  
                  {/* Status Badge */}
                  <div className="absolute top-4 right-4">
                    <Badge className={`font-semibold ${getStatusBadgeColor(project.status)}`}>
                      {project.status}
                    </Badge>
                  </div>

                  {/* Category Badge */}
                  <div className="absolute top-4 left-4">
                    <Badge className="bg-purple-600 text-white font-medium">
                      {project.category}
                    </Badge>
                  </div>
                </div>

                {/* Project Content */}
                <div className="p-6 flex flex-col flex-1">
                  {/* Title */}
                  <h3 className="heading-4 font-bold text-text-primary mb-3 group-hover:text-gradient transition-colors duration-300">
                    {project.title}
                  </h3>

                  {/* Description */}
                  <div 
                    className="text-text-secondary mb-4 line-clamp-3 leading-relaxed rich-text-content"
                    dangerouslySetInnerHTML={{ __html: project.description }}
                  />

                  {/* Technologies */}
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-2">
                      {project.technologies?.map((tech, index) => (
                        <Badge key={index} className="bg-blue-100 text-blue-700 px-3 py-1 text-sm font-medium">
                          {tech}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Impact */}
                  <div className="flex items-start space-x-2 mb-4 flex-grow">
                    <TrendingUp size={16} className="text-emerald-600 mt-1 flex-shrink-0" />
                    <div className="text-sm text-text-secondary">
                      <span className="font-semibold text-emerald-600">Impact:</span>
                      <div 
                        className="inline rich-text-content ml-1"
                        dangerouslySetInnerHTML={{ __html: project.impact }}
                      />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-4 border-t border-gray-200 mt-auto">
                    <Button 
                      onClick={() => handleViewProject(project)}
                      className="btn-base btn-primary btn-sm flex-1"
                    >
                      <Eye size={16} className="mr-2" />
                      View Project
                    </Button>
                    <Button 
                      onClick={() => handleViewProject(project)}
                      className="btn-base btn-secondary btn-sm"
                      variant="ghost"
                    >
                      <Code size={16} />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Empty State */}
          {filteredProjects.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <Code size={48} className="mx-auto" />
              </div>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No projects found</h3>
              <p className="text-gray-500">Try selecting a different category</p>
            </div>
          )}
        </div>
      </div>

      {/* Project Detail Modal */}
      {selectedProject && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative">
            {/* Close Button */}
            <Button
              onClick={() => setSelectedProject(null)}
              className="absolute top-6 right-6 z-10 w-12 h-12 rounded-full bg-white/90 hover:bg-white shadow-lg transition-all duration-200"
              variant="ghost"
            >
              <X size={20} className="text-gray-700" />
            </Button>

            {/* Modal Content */}
            <div className="p-8">
              {/* Project Header */}
              <div className="mb-8">
                <div className="flex flex-wrap gap-3 mb-4">
                  <Badge className="bg-purple-600 text-white font-medium">
                    {selectedProject.category}
                  </Badge>
                  <Badge className={`font-semibold ${getStatusBadgeColor(selectedProject.status)}`}>
                    {selectedProject.status}
                  </Badge>
                </div>
                
                <h1 className="text-4xl font-bold text-text-primary mb-4">
                  {selectedProject.title}
                </h1>
                
                <div 
                  className="text-xl text-text-secondary font-medium leading-relaxed rich-text-content"
                  dangerouslySetInnerHTML={{ __html: selectedProject.description }}
                />
              </div>

              {/* Project Image */}
              {selectedProject.image && (
                <div className="mb-8">
                  <img 
                    src={selectedProject.image} 
                    alt={selectedProject.title}
                    className="w-full h-80 object-cover rounded-2xl shadow-lg"
                    onError={(e) => {
                      e.target.src = '/api/placeholder/600/320';
                    }}
                  />
                </div>
              )}

              {/* Project Details */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-text-primary mb-4">Project Details</h3>
                <div className="prose prose-xl max-w-none">
                  {selectedProject.details ? (
                    <div 
                      className="space-y-6 text-text-secondary leading-relaxed text-lg rich-text-content"
                      dangerouslySetInnerHTML={{ __html: selectedProject.details }}
                    />
                  ) : (
                    <div className="space-y-6 text-text-secondary leading-relaxed text-lg">
                      <p>
                        This project represents a significant advancement in {selectedProject.category.toLowerCase()}, 
                        demonstrating innovative approaches to solving complex business challenges.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Technologies and Impact */}
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-xl font-bold text-text-primary mb-4">Technologies Used</h3>
                  <div className="flex flex-wrap gap-3">
                    {selectedProject.technologies?.map((tech, index) => (
                      <Badge key={index} className="bg-blue-100 text-blue-700 px-4 py-2 text-sm font-medium">
                        {tech}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-text-primary mb-4">Impact & Results</h3>
                  <div className="flex items-start space-x-3">
                    <TrendingUp size={24} className="text-emerald-600 mt-1 flex-shrink-0" />
                    <div className="text-green-700 font-medium rich-text-content">
                      <div dangerouslySetInnerHTML={{ __html: selectedProject.impact }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ProjectsPage;